import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import BugReportSheet from '@/components/BugReportSheet';
import { useAuthStore } from '@/stores/AuthState';
import { openExternal } from '@/utils/browser';
import { feedbackFormUrl } from '@/utils/feedbackForm';
import { useTranslation } from '@/utils/i18n';
import { isBugReportAvailable, type FeedbackTrigger } from '@/utils/sentry';
import { cn } from '@/lib/utils';

/**
 * "Aide-nous à améliorer Racore": a bug goes to Sentry (Racore bottom sheet),
 * an idea to the Google Form with the user's email pre-filled. Shown on Profile
 * and on the + screen. Full-width rows so labels never wrap.
 */
export default function FeedbackCard({
  trigger,
  className,
}: {
  trigger: Exclude<FeedbackTrigger, 'crash'>;
  className?: string;
}) {
  const { t } = useTranslation();
  const language = useAuthStore((s) => s.userInfo.language);
  const email = useAuthStore((s) => s.userInfo.email);
  const [bugOpen, setBugOpen] = useState(false);
  const canReportBug = isBugReportAvailable();

  return (
    <section className={cn('tw:shrink-0', className)} data-testid="feedback-card">
      {/* Section title outside the surface, like Profile's section titles. */}
      <h2 className="tw:mb-3 tw:mt-7 tw:heading-3 tw:text-white">{t('feedback.cardTitle')}</h2>
      <div
        className="tw:overflow-hidden tw:rounded-2xl tw:bg-dark-1"
        data-testid="feedback-card-rows"
      >
        {canReportBug && (
          <FeedbackRow
            emoji="🐞"
            title={t('feedback.bugCta')}
            hint={t('feedback.bugHint')}
            onClick={() => setBugOpen(true)}
            testid="btn-feedback-bug"
          />
        )}
        <FeedbackRow
          emoji="💡"
          title={t('feedback.ideaCta')}
          hint={t('feedback.ideaHint')}
          onClick={() => void openExternal(feedbackFormUrl(language, email))}
          testid="btn-feedback-idea"
          className={canReportBug ? 'tw:border-t tw:border-white/5' : undefined}
        />
      </div>
      {canReportBug && (
        <BugReportSheet open={bugOpen} onClose={() => setBugOpen(false)} trigger={trigger} />
      )}
    </section>
  );
}

function FeedbackRow({
  emoji,
  title,
  hint,
  onClick,
  testid,
  className,
}: {
  emoji: string;
  title: string;
  hint: string;
  onClick: () => void;
  testid: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        'tw:flex tw:min-h-16 tw:w-full tw:items-center tw:gap-3 tw:px-4 tw:py-3 tw:text-left',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-dark-primary tw:text-[22px] tw:leading-none"
      >
        {emoji}
      </span>
      <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
        <span className="tw:text-base tw:font-semibold tw:text-white">{title}</span>
        <span className="tw:caption tw:text-grey-3">{hint}</span>
      </span>
      <ChevronRight size={18} className="tw:shrink-0 tw:text-grey-3" />
    </button>
  );
}
